import { useState, useEffect, useContext, useRef } from 'react';
import { DictionaryResponse } from '../services/dictionary';
import SearchBar from '../components/SearchBar';
import WordDefinition from '../components/WordDefinition';
import { UserContext } from '../store/useStore';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, CalendarHeart, Sparkles, WholeWordIcon, Podcast, Flame, Frown } from 'lucide-react';
import { translateFallback, translateText } from '../hooks/useTranslation';
import { AuthContext } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import WordCard from '../components/WordCard';
import { useWord } from '../contexts/WordContext';
import Button from '../components/common/Button';
import LoginPrompt from './LoginPrompt';
import AudioPlayer from '../components/common/AudioPlayer';
import { updateWordDayStreak, getUserStats, fetchWordDayWeekActivity } from '../services/vocabularyService';
import { FeatureSummary } from '../components/gamification/FeatureSummary';
import PastWordsModal from '../components/PastWordsModal';
import { History } from 'lucide-react';
import WordOfTheDayCard from '../components/WordOfTheDayCard';
import { getWordTranslationOverride } from '../data/wordNuances';
import { db } from '../lib/firebase';
import { ref, push, serverTimestamp } from 'firebase/database';

export default function Day() {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wordData, setWordData] = useState<DictionaryResponse | null>(null);
  const [translatedWord, setTranslatedWord] = useState<string | null>(null);
  const [loginModal, setLoginModal] = useState(false);
  const [wordSaved, setWordSaved] = useState(false);
  const [allActivitiesCompleted, setAllActivitiesCompleted] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [pastWordsModalOpen, setPastWordsModalOpen] = useState(false);
  const [previousStreak, setPreviousStreak] = useState(0);
  const [textToTranslate, setTextToTranslate] = useState<string>('');
  const [wordDayWeekHistory, setWordDayWeekHistory] = useState<boolean[]>([]);
  const { addWord, getAllDayExercises, wordOfDayPoints, setWordOfDayPoints } = useContext(UserContext)
  const { currentUser } = useContext(AuthContext)
  const [wordDayStreak, setWordDayStreak] = useState(0);
  const { 
      currentWord, 
      synonymActivity, 
      fillBlankActivity, 
      trueFalseActivity,
      activitiesCompleted,
      markActivityCompleted,
      loadRandomWord,
      wordLoading,
      loadWordOfTheDay
    } = useWord();
  const { state } = useLocation()
  const navigate = useNavigate();
  const previousWordRef = useRef<string | null>(null);
  const hasAttemptedLoad = useRef(false);

  useEffect(() => {
    if (currentUser?.uid) {
      getUserStats(currentUser.uid).then(async stats => {
        const streak = stats.wordDayStreak || 0;
        setWordDayStreak(streak);
        setPreviousStreak(streak);
        
        // Load week history
        const weekHistory = await fetchWordDayWeekActivity(currentUser.uid);
        setWordDayWeekHistory(weekHistory);
      });
    }
  }, [currentUser]);

  const checkAllActivitiesCompleted = async () => {
    const allDone = await getAllDayExercises(currentWord);
    console.log('All done:', allDone);
    if(allDone) {
      setAllActivitiesCompleted(true);
      if (currentUser?.uid) {
        // Store previous streak before updating
        const currentStats = await getUserStats(currentUser.uid);
        setPreviousStreak(currentStats.wordDayStreak || 0);
        
        await updateWordDayStreak(currentUser.uid, wordOfDayPoints.speak + wordOfDayPoints.fillBlank + wordOfDayPoints.synonym + wordOfDayPoints.trueFalse + wordOfDayPoints.podcast + 100);
        console.log('Day.tsx - updateWordDayStreak completed');
        // Small delay to ensure database write completes
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Refresh streak count and week history
        const updatedStats = await getUserStats(currentUser.uid);
        const newStreak = updatedStats.wordDayStreak || 0;
        setWordDayStreak(newStreak);
        
        // Fetch week history
        const weekHistory = await fetchWordDayWeekActivity(currentUser.uid);
        setWordDayWeekHistory(weekHistory);
        
        // Show summary immediately
        setShowSummary(true);
        
        // Dispatch custom event to notify other components
        console.log('Day.tsx - Dispatching wordDayStreakUpdated event');
        window.dispatchEvent(new CustomEvent('wordDayStreakUpdated'));
      }
    }
  }

  useEffect(() => {
    if (!currentWord && !wordLoading && !hasAttemptedLoad.current) {
      hasAttemptedLoad.current = true;
      loadWordOfTheDay();
      return;
    }

    if (currentWord && currentWord.word !== previousWordRef.current) {
      console.log('Current word:', currentWord);
      
      // Update the previous word reference
      previousWordRef.current = currentWord.word;
      
      // Use the word details already fetched by WordContext
      if (currentWord.details) {
        setWordData(currentWord.details);
        setWordSaved(false);
        const textForTranslation = currentWord.word.replace(/[^\w\s]/gi, '');
        setTextToTranslate(textForTranslation);
        translate(textForTranslation);
      }
      
      checkAllActivitiesCompleted()
    } else if (!currentWord) {
      console.log('No word found');
      previousWordRef.current = null;
    }
  }, [currentWord, wordLoading, loadWordOfTheDay])

  const handleSave = async () => {
    if(!currentUser) {
      setLoginModal(true);
      return;
    }
    if (!wordData) return;

    setIsSaving(true);
    setError(null);
    
    try {
      addWord(wordData)
      setError('Word saved successfully!');
      setWordSaved(true)
    } catch (err) {
      console.error('Error saving word:', err);
      setError('Failed to save word. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const translate = async (text: string) => {
      const override = getWordTranslationOverride(text);
      if (override) {
        setTranslatedWord(override);
        return;
      }
      const translationResult = await translateText(text, 'en', 'pt');
      if (translationResult.toLowerCase() == text.toLowerCase()) {
        translateFallback(text).then((result) => {
          setTranslatedWord(result);
        });
      } else {
        setTranslatedWord(translationResult);
      }
    }

  const handleFlagTranslation = async () => {
    if (!wordData || !translatedWord) return;
    
    // Save to Firebase Realtime Database
    try {
      const flaggedRef = ref(db, 'flagged_translations');
      await push(flaggedRef, {
        word: wordData.word,
        textToTranslate: textToTranslate,
        wrongTranslation: translatedWord,
        timestamp: serverTimestamp(),
        userId: currentUser?.uid || 'anonymous'
      });
      console.log('Flagged translation saved to database');
    } catch (err) {
      console.error('Error saving flagged translation:', err);
    }
    
    // Attempt fallback translation
    try {
      setTranslatedWord('Loading fallback translation...');
      const fallbackResult = await translateFallback(textToTranslate);
      if (fallbackResult) {
        setTranslatedWord(fallbackResult);
      } else {
        setTranslatedWord(translatedWord); // Restore if empty
      }
    } catch (err) {
      console.error('Error fetching fallback translation:', err);
      setTranslatedWord(translatedWord); // Restore on error
    }
  };

  // Show summary screen if all activities completed
  if (showSummary && allActivitiesCompleted && currentUser) {
    return (
      <FeatureSummary
        title="Perfect Lesson!"
        message="You completed all activities for today's word!"
        isPerfect={true}
        currentStreak={wordDayStreak}
        previousStreak={previousStreak}
        weekHistory={wordDayWeekHistory}
        onContinue={() => {
          navigate('/dashboard');
        }}
        continueLabel="Continue Learning"
        onSecondaryAction={() => {
          navigate('/themes');
        }}
        secondaryLabel="Learn New Words"
        colorScheme="pink"
      />
    );
  }

  return (
    <div className='w-full min-h-screen bg-gradient-to-br from-purple-50 to-white pb-16'>
      <Modal 
          isOpen={loginModal} 
          setIsModalOpen={setLoginModal} 
          title='Login or a Create Account'
          message='To save a word you must be logged in!'
          actionText='Login/Register' 
          action={() => navigate('/login')}
          />
      <PastWordsModal 
        isOpen={pastWordsModalOpen}
        onClose={() => setPastWordsModalOpen(false)}
      />
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-8">
        <header className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4 relative">
              <CalendarHeart className="w-12 h-12 text-purple-600" />
              <h1 className="text-4xl font-bold text-gray-900">Word of the Day</h1>

              {/* Streak Badge (Mobile/Desktop friendly) */}
              {currentUser && wordDayStreak > 0 && (
                <div className="absolute top-0 right-0 md:static md:ml-4 bg-pink-100 border border-pink-200 text-pink-700 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-sm animate-pulse transform md:transform-none translate-x-1/4 -translate-y-1/2 md:translate-x-0 md:translate-y-0">
                  <Flame className="w-4 h-4 text-pink-600 fill-pink-600" />
                  <span className="font-bold text-sm">{wordDayStreak}</span>
                </div>
              )}
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Expanda o seu vocabulário com uma palavra por dia!
            </p>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Todos os dias às 9h da manhã, uma nova palavra será apresentada para você!
            </p>
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setPastWordsModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 font-semibold rounded-xl hover:bg-purple-200 transition-colors shadow-sm"
              >
                <History className="w-4 h-4" />
                Ver palavras anteriores
              </button>
            </div>
          </header>
        </div>

        {error && (
          <div className={`p-4 rounded-lg mb-6 ${error.includes('successfully!') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {error}
          </div>
        )}

        {!wordData && wordLoading && (
          <div className="bg-white rounded-xl shadow-lg p-12 flex flex-col justify-center items-center min-h-[400px]">
            <div className="relative">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 border-4 border-purple-200 rounded-full"></div>
              </div>
              <div className="animate-spin w-20 h-20 border-4 border-purple-600 border-t-transparent rounded-full"></div>
            </div>
            <div className="mt-8 text-center">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Loading Word of the Day
              </h3>
              <p className="text-gray-600 animate-pulse">
                Preparing your daily learning experience...
              </p>
              <div className="flex justify-center gap-1 mt-4">
                <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}

        {currentWord?.audioUrl && (
          <div className="mt-6 bg-purple-100 rounded-xl p-6 shadow-lg border border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold text-gray-800 flex items-center">
                <Podcast className="w-6 h-6 mr-2 text-purple-600" />
                Podcast Word of the Day
              </h2>
              <span className="bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full">NEW</span>
            </div>
            <p className="text-gray-600 mb-4">
              Escuta o nosso novo podcast diário sobre a palavra do dia e aprende de forma fácil e divertida!
            </p>
            <div className='w-full max-w-md justify-center items-center mx-auto'>
              <AudioPlayer show={false} audioUrl={currentWord.audioUrl} word={currentWord.word.split('')[0].toUpperCase() + currentWord.word.slice(1)} onEnded={() => setWordOfDayPoints(prev => ({ ...prev, podcast: 50 }))} />
            </div>
          </div>
        )}

        {!currentUser && (
          <div className='mt-6'>
            <LoginPrompt />
          </div> 
        )}

            {!allActivitiesCompleted && currentUser && (<div className="mb-6 mt-6 bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Desafios</h2>
              <p className="text-gray-600 mb-6">Complete os desafios a seguir para reforçar o seu aprendizado e somar pontos no ranking!</p>
              
              <button
                onClick={() => navigate('/activities')}
                className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-400 transition duration-200"
              >
                Ir para atividades
              </button>
            </div>)}

            {allActivitiesCompleted && (
              <div className="mt-5 bg-purple-50 border border-purple-100 rounded-xl p-6 mb-8">
              <div className="flex flex-col items-center text-center md:flex-row md:justify-between md:items-center md:text-left">
                {/* Icon + Text Section */}
                <div className="flex flex-col items-center md:flex-row md:items-center">
                  <div className="bg-purple-100 rounded-full p-3 mb-2 md:mb-0 md:mr-4">
                    <Sparkles className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-purple-900">All activities completed!</h3>
                    <p className="text-purple-700 mt-2">Great job! Try a new word to continue learning.</p>
                  </div>
                </div>
            
                {/* Button Section */}
                <div className="mt-4 md:mt-0 md:ml-auto">
                  <Button
                    onClick={() => navigate('/themes')}
                    className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-400 transition duration-200"
                    rightIcon={<ArrowRight className="h-4 w-4" />}
                  >
                    New Word
                  </Button>
                </div>
              </div>
            </div>            
            )}

        {wordData && (
          <WordDefinition 
            data={wordData} 
            onSave={handleSave}
            isSaving={isSaving}
            translated={translatedWord!}
            wordSaved={wordSaved}
            setWordSaved={setWordSaved}
            themeName={state?.themeName}
            setWordData={setWordData}
            hideClose={true}
            hidePractice={!allActivitiesCompleted}
            onFlagTranslation={handleFlagTranslation}
          />
        )}

        {wordData && currentWord && (
          <WordOfTheDayCard 
            word={currentWord.word}
            definition={wordData.definition || 'Definition not available.'}
          />
        )}
      </div>
    </div>
  );
}